import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDataTransferHorizontalIcon,
  ArrowReloadHorizontalIcon,
  CancelCircleIcon,
  Invoice03Icon,
  Link03Icon,
  MagicWand04Icon,
  MoneyAdd01Icon,
  MoneySend02Icon,
  MoreHorizontalIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import CircularProgress from "@mui/material/CircularProgress";
import { IconButton, Menu, MenuItem } from "@outpave/ds";
import { useFeatureEntitlements } from "@frontegg/react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AccountType from "enums/AccountType";
import {
  useResolvedBankAccounts,
  UseGetBankAccountsResult,
} from "hooks/useGetBankAccounts";
import TransferFundsDrawer from "./TransferFunds";
import AddFundingDrawer from "./AddFundingDrawer";
import AutoBalanceDrawer from "./AutoBalanceDrawer";
import ConnectedFundingAccountDrawer from "./ConnectedFundingAccountDrawer";

/** A drawer the panel around this menu can open on its own. */
export type AccountDrawer = "connectedAccounts" | "autoBalance";

interface AccountActionsMenuProps {
  selectedBankAccountId?: string;
  /** Charge programs repay a statement; prepaid ones move their own funds. */
  charge?: boolean;
  /**
   * A drawer opened from outside the menu: the dashboard panel's "Add account"
   * control, or bank settings' "Balance rules" button. This menu owns all four
   * drawers, so a sibling control asks for one rather than mounting a second
   * copy.
   */
  requested?: AccountDrawer | null;
  onRequestedClose?: () => void;
  /**
   * Render the drawers without the kebab that opens them, for a page whose own
   * controls are the only way in. The drawers still need one owner, so this
   * suppresses the trigger rather than the component.
   */
  hideTrigger?: boolean;
  refreshTransactions: () => void;
  // Forwarded from a parent that already fetched accounts; self-fetches when absent.
  bankAccounts?: UseGetBankAccountsResult;
}

/**
 * The accounts panel's overflow menu, and the owner of the four bank drawers
 * it opens. Which actions it lists is the one thing that differs between a
 * prepaid and a charge program, so the two panels this replaced are now one.
 */
const AccountActionsMenu: React.FC<AccountActionsMenuProps> = ({
  selectedBankAccountId,
  charge = false,
  requested = null,
  onRequestedClose,
  hideTrigger = false,
  refreshTransactions,
  bankAccounts,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isEntitled: featureManageBanks } = useFeatureEntitlements(
    "feature_manage_banks",
  );
  const { isEntitled: featureStatements } =
    useFeatureEntitlements("feature_statements");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openAutoBalanceDrawer, setOpenAutoBalanceDrawer] = useState(false);
  const [openDrawerConnectedFunding, setOpenDrawerConnectedFunding] =
    useState(false);
  const [openTransferDrawer, setOpenTransferDrawer] = useState(false);
  const [openAddFundingDrawer, setOpenAddFundingDrawer] = useState(false);

  const {
    internalAccounts: financialAccounts,
    externalAccounts: allExternalAccounts,
    isLoading: loading,
    refetchAllAccounts,
  } = useResolvedBankAccounts(bankAccounts, {
    accountTypes: [AccountType.FINANCIAL_ACCOUNT, AccountType.BANK],
  });

  // A shared fetch may include PLAID external cards, which aren't valid transfer
  // or funding sources; keep only linked banks.
  const externalBanks = allExternalAccounts.filter(
    (account) => account.accountType === AccountType.BANK,
  );

  const closeMenu = () => setAnchorEl(null);

  const handleCloseDrawers = (refresh?: boolean) => {
    if (refresh) refreshTransactions();

    setOpenTransferDrawer(false);
    setOpenAddFundingDrawer(false);
    setOpenAutoBalanceDrawer(false);
    setOpenDrawerConnectedFunding(false);
    onRequestedClose?.();
  };

  const opens = (open: () => void) => () => {
    open();
    closeMenu();
  };

  const comingSoon = (feature: string) => () => {
    toast.info(t("{{feature}} is coming soon", { feature }));
    closeMenu();
  };

  const prepaidActions = [
    {
      label: t("Add Funds"),
      icon: <HugeiconsIcon icon={MoneyAdd01Icon} size={18} />,
      onClick: opens(() => setOpenAddFundingDrawer(true)),
      shouldNotShow: !featureManageBanks,
    },
    {
      label: t("Transfer Funds"),
      icon: <HugeiconsIcon icon={ArrowDataTransferHorizontalIcon} size={18} />,
      onClick: opens(() => setOpenTransferDrawer(true)),
      shouldNotShow: !featureManageBanks,
    },
    {
      label: t("Auto Balance"),
      icon: <HugeiconsIcon icon={MagicWand04Icon} size={18} />,
      onClick: opens(() => setOpenAutoBalanceDrawer(true)),
      shouldNotShow: !featureManageBanks,
    },
  ];

  const chargeActions = [
    {
      label: t("Statements"),
      icon: <HugeiconsIcon icon={Invoice03Icon} size={18} />,
      onClick: opens(() => navigate("/admin/statements")),
      shouldNotShow: !featureStatements,
    },
    {
      label: t("Pay Now"),
      icon: <HugeiconsIcon icon={MoneySend02Icon} size={18} />,
      onClick: comingSoon(t("Pay Now")),
      shouldNotShow: false,
    },
    {
      label: t("Auto Pay"),
      icon: <HugeiconsIcon icon={ArrowReloadHorizontalIcon} size={18} />,
      onClick: comingSoon(t("Auto Pay")),
      shouldNotShow: false,
    },
  ];

  const actions = [
    ...(charge ? chargeActions : prepaidActions),
    {
      label: t("Connected Accounts"),
      icon: <HugeiconsIcon icon={Link03Icon} size={18} />,
      onClick: opens(() => setOpenDrawerConnectedFunding(true)),
      shouldNotShow: !featureManageBanks,
    },
  ].filter((action) => !action.shouldNotShow);

  return (
    <>
      {!hideTrigger && (
        <>
          <IconButton
            variant="ghost"
            size="small"
            aria-label={t("Account actions")}
            onClick={(event) => {
              if (!loading) setAnchorEl(event.currentTarget);
            }}
          >
            {loading ? (
              <CircularProgress color="primary" size="1.5rem" />
            ) : (
              <HugeiconsIcon icon={MoreHorizontalIcon} size={16} />
            )}
          </IconButton>

          <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={closeMenu}>
            {actions.length > 0 ? (
              actions.map((action) => (
                <MenuItem
                  key={action.label}
                  leading={action.icon}
                  onClick={action.onClick}
                >
                  {action.label}
                </MenuItem>
              ))
            ) : (
              // Entitlements can leave the menu with nothing in it. The row used to
              // be clickable and only closed the menu; disabled says the same thing
              // without offering a click that does nothing.
              <MenuItem
                disabled
                leading={<HugeiconsIcon icon={CancelCircleIcon} size={18} />}
              >
                {t("No Actions Possible")}
              </MenuItem>
            )}
          </Menu>
        </>
      )}

      <TransferFundsDrawer
        open={openTransferDrawer}
        handleCloseDrawer={handleCloseDrawers}
        fromAccountsData={financialAccounts}
        toAccountsData={externalBanks}
        refetchAccounts={refetchAllAccounts}
      />

      <AddFundingDrawer
        selectedBankAccountId={selectedBankAccountId}
        open={openAddFundingDrawer}
        handleCloseDrawer={handleCloseDrawers}
        toAccountsData={financialAccounts}
        bankAccountsData={externalBanks}
        refetchAccounts={refetchAllAccounts}
      />

      <AutoBalanceDrawer
        open={openAutoBalanceDrawer || requested === "autoBalance"}
        handleCloseDrawer={handleCloseDrawers}
        fromAccountsData={externalBanks}
        toAccountsData={financialAccounts}
        refetchAccounts={refetchAllAccounts}
      />

      <ConnectedFundingAccountDrawer
        open={openDrawerConnectedFunding || requested === "connectedAccounts"}
        handleCloseDrawer={handleCloseDrawers}
        bankAccountsData={externalBanks}
        refetchAccounts={refetchAllAccounts}
      />
    </>
  );
};

export default AccountActionsMenu;
